/**
 * Peças do sorteio usadas pela página pública e pelo painel interno do DTP
 * (`operacional/sorteios`): hash copiável, selo de conferência e a
 * verificação feita pelo servidor. Uma cópia só — regra 1 de interface.
 */

import { type ReactElement, type ReactNode } from 'react';
import { CircleCheck, CircleMinus, CircleX } from 'lucide-react';

import { CopyableText } from '@/ui/CopyableText';

import { abreviarHash, dataHora, type VerificacaoDoSorteio } from './contrato';
import type { ResultadoDaChecagem } from './verificacao';

/** Hash inteiro (ou abreviado, em tabela), com botão de copiar o valor completo. */
export function HashCopiavel({
  valor,
  rotulo,
  abreviado = false,
}: {
  valor: string | null | undefined;
  rotulo: string;
  abreviado?: boolean;
}): ReactElement {
  if (!valor) return <span className="text-muted-foreground">—</span>;
  return (
    <CopyableText
      value={valor}
      display={abreviado ? abreviarHash(valor) : valor}
      label={`Copiar ${rotulo}`}
      className="min-w-0 max-w-full items-start [&>span:first-child]:break-all [&>span:first-child]:text-xs"
    />
  );
}

const APRESENTACAO: Record<
  ResultadoDaChecagem,
  { Icone: typeof CircleCheck; texto: string; classe: string }
> = {
  CONFERE: { Icone: CircleCheck, texto: 'Confere', classe: 'text-success' },
  NAO_CONFERE: { Icone: CircleX, texto: 'Não confere', classe: 'text-destructive' },
  NAO_SE_APLICA: {
    Icone: CircleMinus,
    texto: 'Não se aplica ainda',
    classe: 'text-muted-foreground',
  },
};

/** Ícone e palavra — a cor nunca é o único sinal. */
export function SeloDeConferencia({ resultado }: { resultado: ResultadoDaChecagem }): ReactElement {
  const { Icone, texto, classe } = APRESENTACAO[resultado];
  return (
    <span className={`inline-flex items-center gap-1 text-sm font-medium ${classe}`}>
      <Icone className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="text-foreground">{texto}</span>
    </span>
  );
}

function resultadoDe(valor: boolean | null): ResultadoDaChecagem {
  if (valor === null) return 'NAO_SE_APLICA';
  return valor ? 'CONFERE' : 'NAO_CONFERE';
}

function Linha({ rotulo, children }: { rotulo: string; children: ReactNode }): ReactElement {
  return (
    <li className="flex flex-col gap-1 border-b py-2 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-sm">{rotulo}</span>
      {children}
    </li>
  );
}

/** O que `sorteio_verificar` respondeu — a conferência do próprio sistema. */
export function ConferenciaDoServidor({ v }: { v: VerificacaoDoSorteio }): ReactElement {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium" aria-live="polite">
        {v.confere
          ? 'O servidor recalculou o sorteio e tudo confere.'
          : 'O servidor encontrou divergência no sorteio.'}
      </p>
      <ul>
        <Linha rotulo="Semente revelada × compromisso">
          <SeloDeConferencia resultado={resultadoDe(v.sementeConfere)} />
        </Linha>
        <Linha rotulo="Lista de participantes congelada">
          <SeloDeConferencia resultado={resultadoDe(v.universoConfere)} />
        </Linha>
        <Linha rotulo="Resultado recalculado × gravado">
          <SeloDeConferencia resultado={resultadoDe(v.resultadoConfere)} />
        </Linha>
        <Linha rotulo="Trilha de eventos encadeada">
          <SeloDeConferencia resultado={resultadoDe(v.trilhaIntegra)} />
        </Linha>
      </ul>
      {v.divergencias.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-5 text-sm">
          {v.divergencias.map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ul>
      )}
      <p className="text-muted-foreground text-xs">Verificado em {dataHora(v.verificadoEm)}.</p>
    </div>
  );
}
